import { useState, useEffect } from 'react';
import orderBy from 'lodash/orderBy';
import {
  TaxExemptionCertificate,
  CertificateAttribute,
} from 'src/types/TaxExemptionCertificate';
import { SortOrder } from 'src/components/Table/SortableColumn';
import { SortConfig } from 'src/components/enterprise/features/TaxExemptions/ExemptionCertificateList/sortConfiguration';
import {
  fetchCertificates,
  transform,
} from 'src/components/enterprise/features/TaxExemptions/taxExemptService';
import { pushErrorFromAPI } from 'src/utilities/notifications';

type UseSortTaxExemptionCertificates = {
  sortBy: (sortField: CertificateAttribute) => void;
  sortConfig: SortConfig;
  sortedCertificates: TaxExemptionCertificate[];
};

export const useSortTaxExemptionCertificates = (
  certificates: TaxExemptionCertificate[],
  config: SortConfig
): UseSortTaxExemptionCertificates => {
  const [sortConfig, setSortConfig] = useState<SortConfig>(config);

  const sortBy = (sortField: CertificateAttribute): void => {
    let sortOrder = SortOrder.ASC;
    const previousSortField = sortField;

    if (
      sortConfig.sortField === sortField &&
      sortConfig.sortOrder === SortOrder.ASC
    ) {
      sortOrder = SortOrder.DESC;
    }

    const previousSortOrder = sortOrder;

    setSortConfig({
      sortField,
      sortOrder,
      previousSortField,
      previousSortOrder,
    });
  };

  const transformSortOrder = (sortOrder: SortOrder): 'asc' | 'desc' => {
    if (sortOrder === SortOrder.ASC) {
      return 'asc';
    }
    return 'desc';
  };

  const sortedCertificates = orderBy(
    [...certificates],
    [sortConfig.sortField],
    [transformSortOrder(sortConfig.sortOrder)]
  );
  return { sortBy, sortConfig, sortedCertificates };
};

type UseFetchExemptionCertificatesForProps = {
  certificates: TaxExemptionCertificate[];
  isLoading: boolean;
};

export const useFetchTaxExemptionCertificatesForCorp = (
  corpId: string
): UseFetchExemptionCertificatesForProps => {
  const [certificates, setCertificates] = useState<TaxExemptionCertificate[]>(
    []
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(true);

    const doFetch = async () => {
      try {
        const responseData = await fetchCertificates(corpId);
        setCertificates(transform(responseData));
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        pushErrorFromAPI(error);
      }
    };

    doFetch();
  }, [corpId]);

  return { certificates, isLoading };
};

type UsePaginatedTaxExemptionCertificates = {
  currentPage: number;
  currentPageCertificates: TaxExemptionCertificate[];
  setCurrentPage: (pageNumber: number) => void;
};

export const usePaginatedTaxExemptionCertificates = (
  certificates: TaxExemptionCertificate[],
  pageSize: number
): UsePaginatedTaxExemptionCertificates => {
  const [currentPage, setCurrentPage] = useState(1);
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  const currentPageCertificates = certificates.slice(start, end);

  return {
    currentPage,
    currentPageCertificates,
    setCurrentPage,
  };
};
